import React, { useState, useEffect } from 'react';
import { useSpreadsheetStore } from './store/spreadsheetStore';
import { Functions } from '@mui/icons-material';
import './FormulaBar.css';

const FormulaBar = () => {
  const {
    getActiveSheet,
    updateCell,
    updateFormula,
    updateCellValue,
    recalculateAllFormulas,
    debugFormula,
    setFormulaBarValue,
    formulaBarValue,
    isEditing,
    applyFormulaOnEnter,
    getSelectionFormula
  } = useSpreadsheetStore();

  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isMultiSelection, setIsMultiSelection] = useState(false);

  const activeSheet = getActiveSheet();
  const { activeCell, selection } = activeSheet;
  const cell = activeSheet.grid[activeCell.row]?.[activeCell.col];

  // Çoklu seçim kontrolü
  useEffect(() => {
    const isMulti = !(selection.startRow === selection.endRow && selection.startCol === selection.endCol);
    setIsMultiSelection(isMulti);
  }, [selection]);

  // Aktif hücre değiştiğinde formula bar'ı güncelle
  useEffect(() => {
    if (cell) {
      const displayValue = cell.formula || cell.value || '';
      setInputValue(displayValue);
      setFormulaBarValue(displayValue);
    }
  }, [activeCell.row, activeCell.col, cell]);

  // Seçim değiştiğinde formula bar'ı güncelle
  useEffect(() => {
    const selectionFormula = getSelectionFormula();
    setInputValue(selectionFormula);
    setFormulaBarValue(selectionFormula);
  }, [selection, getSelectionFormula]);

  // Formül önerileri
  const formulaSuggestions = [
    // Matematiksel fonksiyonlar
    { name: 'SUM', description: 'Toplam hesaplar', example: '=SUM(A1:A10)', category: 'Math' },
    { name: 'AVERAGE', description: 'Ortalama hesaplar', example: '=AVERAGE(B2:B5)', category: 'Math' },
    { name: 'COUNT', description: 'Sayıları sayar', example: '=COUNT(C1:C20)', category: 'Math' },
    { name: 'MAX', description: 'Maksimum değer', example: '=MAX(D1:D10)', category: 'Math' },
    { name: 'MIN', description: 'Minimum değer', example: '=MIN(E1:E10)', category: 'Math' },
    { name: 'ROUND', description: 'Sayıyı yuvarlar', example: '=ROUND(A1,2)', category: 'Math' },
    { name: 'ABS', description: 'Mutlak değer', example: '=ABS(A1)', category: 'Math' },
    { name: 'SQRT', description: 'Karekök', example: '=SQRT(A1)', category: 'Math' },
    { name: 'POWER', description: 'Üs alma', example: '=POWER(A1,2)', category: 'Math' },
    { name: 'MOD', description: 'Modülo', example: '=MOD(A1,2)', category: 'Math' },
    
    // Mantık fonksiyonları
    { name: 'IF', description: 'Koşullu ifade', example: '=IF(A1>10,"Büyük","Küçük")', category: 'Logic' },
    { name: 'AND', description: 'Mantıksal VE', example: '=AND(A1>0,A1<100)', category: 'Logic' },
    { name: 'OR', description: 'Mantıksal VEYA', example: '=OR(A1>0,B1>0)', category: 'Logic' },
    { name: 'NOT', description: 'Mantıksal DEĞİL', example: '=NOT(A1>0)', category: 'Logic' },
    { name: 'ISNUMBER', description: 'Sayı kontrolü', example: '=ISNUMBER(A1)', category: 'Logic' },
    { name: 'ISBLANK', description: 'Boş kontrolü', example: '=ISBLANK(A1)', category: 'Logic' },
    
    // Metin fonksiyonları
    { name: 'CONCATENATE', description: 'Metinleri birleştirir', example: '=CONCATENATE(A1," ",B1)', category: 'Text' },
    { name: 'LEFT', description: 'Soldan karakter al', example: '=LEFT(A1,3)', category: 'Text' },
    { name: 'RIGHT', description: 'Sağdan karakter al', example: '=RIGHT(A1,3)', category: 'Text' },
    { name: 'MID', description: 'Ortadan karakter al', example: '=MID(A1,2,3)', category: 'Text' },
    { name: 'LEN', description: 'Karakter sayısı', example: '=LEN(A1)', category: 'Text' },
    { name: 'UPPER', description: 'Büyük harfe çevir', example: '=UPPER(A1)', category: 'Text' },
    { name: 'LOWER', description: 'Küçük harfe çevir', example: '=LOWER(A1)', category: 'Text' },
    { name: 'TRIM', description: 'Boşlukları temizle', example: '=TRIM(A1)', category: 'Text' },
    
    // Tarih fonksiyonları
    { name: 'TODAY', description: 'Bugünün tarihi', example: '=TODAY()', category: 'Date' },
    { name: 'NOW', description: 'Şu anki tarih ve saat', example: '=NOW()', category: 'Date' },
    { name: 'YEAR', description: 'Yıl', example: '=YEAR(A1)', category: 'Date' },
    { name: 'MONTH', description: 'Ay', example: '=MONTH(A1)', category: 'Date' },
    { name: 'DAY', description: 'Gün', example: '=DAY(A1)', category: 'Date' }
  ];

  // Formül önerilerini filtrele
  const filteredSuggestions = formulaSuggestions.filter(suggestion =>
    suggestion.name.toLowerCase().includes(inputValue.toLowerCase()) ||
    suggestion.description.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setFormulaBarValue(value);
    
    // Formül önerilerini göster
    if (value.startsWith('=') && value.length > 1) {
      setShowSuggestions(true);
      setSuggestionIndex(0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestionIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selectedSuggestion = filteredSuggestions[suggestionIndex];
        const newValue = inputValue.replace(/=.*$/, `=${selectedSuggestion.name}(`);
        setInputValue(newValue);
        setFormulaBarValue(newValue);
        setShowSuggestions(false);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Formülü uygula
      applyFormulaOnEnter(inputValue);
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      // Formül yazımını iptal et
      const originalValue = getSelectionFormula();
      setInputValue(originalValue);
      setFormulaBarValue(originalValue);
      setShowSuggestions(false);
    }
  };

  const handleApplyFormula = () => {
    // Formül mü yoksa değer mi kontrol et
    if (inputValue && inputValue.startsWith('=')) {
      // Formül ise updateFormula kullan
      updateFormula(activeCell.row, activeCell.col, inputValue);
    } else {
      // Değer ise updateCellValue kullan
      updateCellValue(activeCell.row, activeCell.col, inputValue);
    }
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    const newValue = inputValue.replace(/=.*$/, `=${suggestion.name}(`);
    setInputValue(newValue);
    setFormulaBarValue(newValue);
    setShowSuggestions(false);
  };

  const handleApplyButtonClick = () => {
    applyFormulaOnEnter(inputValue);
    setShowSuggestions(false);
  };

  const handleCancelButtonClick = () => {
    const originalValue = getSelectionFormula();
    setInputValue(originalValue);
    setFormulaBarValue(originalValue);
    setShowSuggestions(false);
  };

  const getCellAddress = () => {
    const col = String.fromCharCode(65 + activeCell.col);
    const row = activeCell.row + 1;
    return `${col}${row}`;
  };

  return (
    <div className="formula-bar">
      <div className="formula-bar-left">
        <Functions className="formula-icon" />
        <div className="cell-address">
          {isMultiSelection ? (
            <span className="multi-selection">
              {`${String.fromCharCode(65 + selection.startCol)}${selection.startRow + 1}:${String.fromCharCode(65 + selection.endCol)}${selection.endRow + 1}`}
            </span>
          ) : (
            `${String.fromCharCode(65 + activeCell.col)}${activeCell.row + 1}`
          )}
        </div>
      </div>
      
      <div className="formula-input-container">
        <input
          type="text"
          className="formula-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(inputValue.startsWith('=') && inputValue.length > 1)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={isMultiSelection ? "Seçili hücrelere formül uygula..." : "Formül veya değer girin..."}
        />
        
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="formula-suggestions">
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={suggestion.name}
                className={`suggestion-item ${index === suggestionIndex ? 'selected' : ''}`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setSuggestionIndex(index)}
              >
                <div className="suggestion-name">{suggestion.name}</div>
                <div className="suggestion-description">{suggestion.description}</div>
                <div className="suggestion-example">{suggestion.example}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="formula-bar-right">
        {isMultiSelection && (
          <div className="selection-info">
            <span className="selection-count">
              {Math.abs(selection.endRow - selection.startRow + 1) * Math.abs(selection.endCol - selection.startCol + 1)} hücre seçili
            </span>
          </div>
        )}
        
        <div className="formula-actions">
          <button 
            className="apply-button" 
            onClick={handleApplyButtonClick}
            title="Formülü uygula (Enter)"
          >
            ✓
          </button>
          <button 
            className="cancel-button" 
            onClick={handleCancelButtonClick}
            title="İptal et (Esc)"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormulaBar;
